import type { UnlayerDesign } from "../types/unlayer-design";
import { isUUID, type UUID, uuidv4 } from "../../../utils/uuid";

type UnlayerPollValues = {
  pollId?: string;
  question?: string;
  pollOptions?: string;
  [key: string]: unknown;
};

type UnlayerPollBlock = {
  type?: string;
  values?: UnlayerPollValues;
};

type UnlayerPollOptionValue = {
  id: string;
  text: string;
};

type PreparedUnlayerPoll = {
  pollId: UUID;
  question: string;
  options: { id: UUID; text: string; ordering: number }[];
};

function cloneDesign(design: UnlayerDesign): UnlayerDesign {
  return JSON.parse(JSON.stringify(design)) as UnlayerDesign;
}

function isPollOption(value: unknown): value is UnlayerPollOptionValue {
  return (
    !!value &&
    typeof value === "object" &&
    typeof (value as { id?: unknown }).id === "string" &&
    typeof (value as { text?: unknown }).text === "string"
  );
}

function parsePollOptions(value: string | undefined): { id: UUID; text: string }[] {
  if (!value) return [];

  try {
    const options = JSON.parse(value) as unknown;
    if (!Array.isArray(options) || !options.every(isPollOption)) return [];

    return options
      .map((option) => ({ id: option.id, text: option.text.trim() }))
      .filter((option): option is { id: UUID; text: string } => {
        return isUUID(option.id) && option.text.length > 0;
      });
  } catch {
    return [];
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object";
}

export function getUnlayerPollBlocks(design: UnlayerDesign): UnlayerPollBlock[] {
  const blocks: UnlayerPollBlock[] = [];

  function collect(value: unknown): void {
    if (Array.isArray(value)) {
      value.forEach(collect);
      return;
    }

    if (!isRecord(value)) return;

    if (typeof (value as UnlayerPollBlock).values?.pollOptions === "string") {
      blocks.push(value);
      return;
    }

    Object.values(value).forEach(collect);
  }

  collect(design);
  return blocks;
}

export function prepareUnlayerPollDesign(
  sourceDesign: UnlayerDesign,
  existingPollIds: readonly UUID[] = [],
): {
  design: UnlayerDesign;
  polls: PreparedUnlayerPoll[];
} {
  const design = cloneDesign(sourceDesign);
  const polls: PreparedUnlayerPoll[] = [];

  for (const block of getUnlayerPollBlocks(design)) {
    const values = block.values;
    if (!values) continue;

    const question = values.question?.trim();
    const options = parsePollOptions(values.pollOptions);
    if (!question || options.length === 0) continue;

    const existingPollId: UUID | undefined = existingPollIds[polls.length];
    const pollId: UUID = isUUID(values.pollId)
      ? values.pollId
      : isUUID(existingPollId)
        ? existingPollId
        : uuidv4();
    block.values = { ...values, pollId };
    polls.push({
      pollId,
      question,
      options: options.map((option, index) => ({ ...option, ordering: index + 1 })),
    });
  }

  return { design, polls };
}
